import { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { captureInvoiceImage } from "@/utils/captureInvoiceImage";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { InvoiceTemplate } from "@/components/dashboard/invoices/InvoiceTemplate";
import api from "@/utils/api";
import { Invoice } from "@/types/invoice";
import { RiDownload2Line, RiLoader4Line } from "@remixicon/react";
import { FullPageLoader } from "@/components/ui/full-page-loader";

interface PublicInvoiceResponse {
  invoice: Invoice;
  store: {
    storeName?: string;
    businessName?: string;
    logo?: string;
    currency?: string;
    address?: {
      street?: string;
      city?: string;
      state?: string;
      country?: string;
    };
  };
}

export function PublicInvoiceViewPage() {
  const { id } = useParams<{ id: string }>();
  const invoiceRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const { data, isLoading, error } = useQuery<PublicInvoiceResponse>({
    queryKey: ["public-invoice", id],
    queryFn: async () => {
      const { data } = await api.get(`/api/invoices/public/${id}`);
      return data;
    },
    enabled: !!id,
    retry: false,
  });

  const handleDownloadImage = async (invoiceNumber?: string) => {
    if (!invoiceRef.current) return;
    setIsDownloading(true);
    try {
      await captureInvoiceImage(
        invoiceRef.current,
        `invoice-${invoiceNumber ?? id}.png`,
      );
    } catch {
      toast.error("Failed to download invoice image");
    } finally {
      setIsDownloading(false);
    }
  };

  if (isLoading) {
    return <FullPageLoader text="Loading invoice" variant="page" />;
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6 text-center">
        <div>
          <div className="text-2xl font-bold mb-2">Invoice not found</div>
          <div className="text-muted-foreground">The invoice you are looking for does not exist or is no longer available.</div>
        </div>
      </div>
    );
  }

  const { invoice, store } = data;
  const business = {
    name: store.storeName || store.businessName || "Business",
    logo: store.logo,
    currency: store.currency || invoice.currency || "NGN",
    address: [store.address?.street, store.address?.city, store.address?.state, store.address?.country]
      .filter(Boolean)
      .join(", "),
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      {/* Action bar */}
      <div className="max-w-3xl mx-auto mb-4 flex justify-between items-center">
        <div className="text-sm font-medium text-muted-foreground">{business.name}</div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => handleDownloadImage(invoice.invoiceNumber)} disabled={isDownloading}>
            {isDownloading ? (
              <RiLoader4Line className="h-4 w-4 mr-1.5 animate-spin" />
            ) : (
              <RiDownload2Line className="h-4 w-4 mr-1.5" />
            )}
            {isDownloading ? "Saving…" : "Download Image"}
          </Button>
        </div>
      </div>

      {/* Invoice */}
      <div ref={invoiceRef} className="max-w-3xl mx-auto bg-white rounded-xl shadow-sm overflow-hidden">
        <InvoiceTemplate invoice={invoice} business={business} />
      </div>

      {/* Footer */}
      <div className="max-w-3xl mx-auto mt-6 text-center text-xs text-muted-foreground">
        Powered by <a href="https://sqale.shop" className="underline">Sqaleshop</a>
      </div>
    </div>
  );
}
